import { Component, Input, Output, EventEmitter, signal, OnInit, OnChanges, SimpleChanges, computed, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '../../directives/TranslatePipe';

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './date-range-picker.component.html'
})
export class DateRangePickerComponent implements OnInit, OnChanges {
  private _dateRangeSignal = signal<DateRange>({ start: null, end: null });

  @Input() set startDate(date: Date) {
    // Ne pas ecraser une selection en cours : la liste renvoie ce que le calendrier vient
    // d'emettre, et sans ce garde le second clic repartirait sur une nouvelle plage.
    if (this.isSelectingRange) return;
    this._dateRangeSignal.update(val => ({ ...val, start: date }));
    this.updateCalendarView(date);
  }
  @Input() set endDate(date: Date) {
    if (this.isSelectingRange) return;
    this._dateRangeSignal.update(val => ({ ...val, end: date }));
  }

  // Debut choisi au calendrier, fin pas encore : l'utilisateur est en train de dessiner sa plage.
  // Un drapeau explicite plutot que "start sans end" : au premier rendu, startDate est recu avant
  // endDate, et ce dernier etait alors ignore (fin affichee "...").
  private isSelectingRange = false;

  get _value(): DateRange {
    return this._dateRangeSignal();
  }

  @Output() dateRangeChange = new EventEmitter<{ startDate: Date; endDate: Date }>();

  show = signal(false);

  private readonly today = new Date();
  viewYear = signal(0);
  viewMonth = signal(0);

  currentMonthAndYear = computed(() => new Date(this.viewYear(), this.viewMonth()));

  isDateRangeSelection = computed(() => {
    const { start, end } = this._dateRangeSignal();
    return start && end;
  });

  isStartDateOnlySelection = computed(() => {
    const { start, end } = this._dateRangeSignal();
    return start && !end;
  });

  isNoDateSelection = computed(() => {
    const { start, end } = this._dateRangeSignal();
    return !start && !end;
  });

  constructor(private elementRef: ElementRef) { }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      if (this.isSelectingRange) {
        this.isSelectingRange = false;
        this._dateRangeSignal.update(val => ({ ...val, end: val.start }));
        this.emitDateRange();
      }
      this.show.set(false);
    }
  }

  ngOnInit(): void {
    if (this._value.start) {
      this.updateCalendarView(this._value.start);
    } else {
      this.updateCalendarView(this.today);
    }
    // Pas d'emission initiale : le parent connait deja la plage via ses inputs.
    // Emettre ici renverrait "aujourd'hui" par-dessus la plage restauree des filtres.
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startDate'] && changes['startDate'].currentValue) {
      this.updateCalendarView(changes['startDate'].currentValue);
    }
    // Pas d'emission ici : le parent renvoie une nouvelle Date a chaque emission, ce qui
    // recreerait un ngOnChanges et boucherait sans fin.
  }

  private updateCalendarView(date: Date): void {
    this.viewYear.set(date.getFullYear());
    this.viewMonth.set(date.getMonth());
  }

  daysInMonth(year: number, month: number): number {
    return new Date(year, month + 1, 0).getDate();
  }

  get calendar(): (number | null)[] {
    const year = this.viewYear();
    const month = this.viewMonth();
    const days = this.daysInMonth(year, month);

    const firstDay = new Date(year, month, 1).getDay();
    const adjustedFirstDay = (firstDay + 6) % 7; // (0=Sun -> 6, 1=Mon -> 0, ..., 6=Sat -> 5)

    const blanks = Array(adjustedFirstDay).fill(null);

    return [...blanks, ...Array.from({ length: days }, (_, i) => i + 1)];
  }

  prevMonth(): void {
    const m = this.viewMonth();
    const y = this.viewYear();
    if (m === 0) {
      this.viewMonth.set(11);
      this.viewYear.set(y - 1);
    } else {
      this.viewMonth.set(m - 1);
    }
  }

  nextMonth(): void {
    const m = this.viewMonth();
    const y = this.viewYear();
    if (m === 11) {
      this.viewMonth.set(0);
      this.viewYear.set(y + 1);
    } else {
      this.viewMonth.set(m + 1);
    }
  }

  select(day: number | null): void {
    if (!day) return;

    const date = new Date(this.viewYear(), this.viewMonth(), day);
    date.setHours(0, 0, 0, 0);

    const start = this._value.start;
    if (!this.isSelectingRange || !start) {
      this.isSelectingRange = true;
      this._dateRangeSignal.set({ start: date, end: null });
    } else {
      this.isSelectingRange = false;
      if (date < start) {
        this._dateRangeSignal.set({ start: date, end: start });
      } else {
        this._dateRangeSignal.set({ start: start, end: date });
      }
      // La plage est complete : on referme le calendrier plutot que de laisser
      // l'utilisateur le refermer a la main.
      this.show.set(false);
    }

    this.emitDateRange();
  }

  isSelected(day: number | null): boolean {
    if (!day) return false;
    const d = new Date(this.viewYear(), this.viewMonth(), day);
    d.setHours(0, 0, 0, 0);

    const { start, end } = this._value;
    if (!start) return false;
    if (start && !end) return d.getTime() === start.getTime();
    return d.getTime() >= start.getTime() && d.getTime() <= (end ? end.getTime() : start.getTime());
  }

  isStartDate(day: number | null): boolean {
    if (!day || !this._value.start) return false;
    const d = new Date(this.viewYear(), this.viewMonth(), day);
    d.setHours(0, 0, 0, 0);
    return this._value.start.getTime() === d.getTime();
  }

  isEndDate(day: number | null): boolean {
    if (!day || !this._value.end) return false;
    const d = new Date(this.viewYear(), this.viewMonth(), day);
    d.setHours(0, 0, 0, 0);
    return this._value.end.getTime() === d.getTime();
  }

  isBetweenSelectedRange(day: number | null): boolean {
    if (!day || !this._value.start || !this._value.end) return false;
    const d = new Date(this.viewYear(), this.viewMonth(), day);
    d.setHours(0, 0, 0, 0);

    return d.getTime() > this._value.start.getTime() && d.getTime() < this._value.end.getTime();
  }

  private emitDateRange(): void {
    if (this._value.start && this._value.end) {
      const finalEndDate = new Date(this._value.end);
      finalEndDate.setHours(23, 59, 59, 999);
      this.dateRangeChange.emit({ startDate: this._value.start, endDate: finalEndDate });
    } else if (this._value.start) {
      const singleDayEndDate = new Date(this._value.start);
      singleDayEndDate.setHours(23, 59, 59, 999);
      this.dateRangeChange.emit({ startDate: this._value.start, endDate: singleDayEndDate });
    }
  }

  public get monthName(): string {

    let month = this.currentMonthAndYear().toLocaleString('en-US', { month: 'long' });
    return month;
  }

  get formattedDateRange(): string {
    const start = this._value.start;
    const end = this._value.end;

    if (start && end) {
      if (start.getTime() === end.getTime()) {
        return start.toLocaleDateString(); // Single day
      }
      return `${start.toLocaleDateString()} → ${end.toLocaleDateString()}`;
    } else if (start) {
      return `${start.toLocaleDateString()} → ...`;
    }
    return 'select-range-date';
  }
}
